import { useState, useRef, useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Controller as RHFController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import type { ThemeClasses, PointOfInterestFormValues } from "../types";
import { getCustomSelectStyles } from "../../../../shared";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

interface PointOfInterestFormProps {
  form: UseFormReturn<PointOfInterestFormValues>;
  isEdit: boolean;
  loadingSubmit: boolean;
  hasRealChanges: () => boolean;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onCoordinateChange: (lat: number, lng: number) => void;
  onSubmit: (data: PointOfInterestFormValues) => void;
  onCancel: () => void;
  mapComponent?: React.ReactNode;
}

const getCategoryOptions = (t: (key: string) => string) => [
  { value: "residence", label: t("pointsOfInterest.categories.residence") },
  { value: "client", label: t("pointsOfInterest.categories.client") },
  { value: "work", label: t("pointsOfInterest.categories.work") },
  { value: "warehouse", label: t("pointsOfInterest.categories.warehouse") },
  { value: "office", label: t("pointsOfInterest.categories.office") },
  { value: "gas_station", label: t("pointsOfInterest.categories.gas_station") },
  { value: "other", label: t("pointsOfInterest.categories.other") },
];

export function PointOfInterestForm({
  form,
  isEdit,
  loadingSubmit,
  hasRealChanges,
  themeClasses,
  isDarkMode,
  onCoordinateChange,
  onSubmit,
  onCancel,
  mapComponent,
}: PointOfInterestFormProps) {
  const { t } = useTranslation("adminSections");
  const statuses = useStatusesStore((state) => state.statuses);
  const statusOptions = getStatusOptionsForEntity(statuses, "pointsOfInterest", t);
  const categoryOptions = getCategoryOptions(t);

  // Google Places Autocomplete
  const placesLibrary = useMapsLibrary("places");
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [addressValue, setAddressValue] = useState<string>(form.getValues("address") || "");

  // Initialize autocomplete when places library loads
  useEffect(() => {
    if (!placesLibrary || !addressInputRef.current) return;
    if (autocompleteRef.current) return; // Already initialized

    const autocomplete = new placesLibrary.Autocomplete(addressInputRef.current, {
      fields: ["formatted_address", "geometry", "name"],
      types: ["address"],
    });

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const address = place.formatted_address || place.name || "";

        // Update form values
        form.setValue("latitude", lat, { shouldValidate: true });
        form.setValue("longitude", lng, { shouldValidate: true });
        form.setValue("address", address, { shouldValidate: true });
        setAddressValue(address);

        // Notify parent about coordinate change
        onCoordinateChange(lat, lng);
      }
    });

    autocompleteRef.current = autocomplete;

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [placesLibrary, form, onCoordinateChange]);

  // Sync address value with form
  useEffect(() => {
    const formAddress = form.getValues("address");
    if (formAddress !== addressValue) {
      setAddressValue(formAddress || "");
    }
  }, [form.watch("address")]);

  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors, isValid },
  } = form;

  const latitude = watch("latitude");
  const longitude = watch("longitude");
  const shared = watch("shared");

  const inputClassName = `w-full text-xs 2xl:text-base px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
    } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text}`;

  const hasValidLocation = latitude !== 0 && longitude !== 0;

  return (
    <form
      className="flex flex-col w-full gap-4"
      onSubmit={handleSubmit(onSubmit)}
    >

      {/* Nombre del POI */}
      <div className="flex flex-col gap-2">
        <label className={`block text-sm font-medium ${themeClasses.text}`}>
          {t("pointsOfInterest.fields.name")} <span className="text-red-500">*</span>
        </label>
        <input
          {...register("name", { required: true })}
          type="text"
          className={`${inputClassName} ${errors.name
            ? "ring-2 ring-red-500"
            : "focus:ring-2 focus:ring-primary-500"
            } focus:outline-none`}
          placeholder={t("pointsOfInterest.form.namePlaceholder")}
        />
        {errors.name && (
          <span className="text-red-500 text-xs">
            {t("pointsOfInterest.form.requiredField")}
          </span>
        )}
      </div>

      {/* Estado */}
      <div className="flex flex-col gap-2">
        <label className={`block text-sm font-medium ${themeClasses.text}`}>
          {t("pointsOfInterest.fields.status")} <span className="text-red-500">*</span>
        </label>
        <RHFController
          name="status_id"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              {...field}
              options={statusOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("pointsOfInterest.form.selectStatus")}
              value={statusOptions.find((opt) => opt.value === field.value) || null}
              onChange={(option: any) => field.onChange(option?.value || "")}
              isSearchable={false}
              noOptionsMessage={() => t("pointsOfInterest.form.noStatusesAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.status_id && (
          <span className="text-red-500 text-xs">
            {t("pointsOfInterest.form.requiredField")}
          </span>
        )}
      </div>

      {/* Dirección con Autocompletado */}
      <div className="flex flex-col gap-2">
        <label className={`block text-sm font-medium ${themeClasses.text}`}>
          {t("pointsOfInterest.fields.address")} ({t("pointsOfInterest.form.optional")})
        </label>
        <div className="relative">
          <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
          <input
            ref={addressInputRef}
            type="text"
            value={addressValue}
            onChange={(e) => {
              setAddressValue(e.target.value);
              form.setValue("address", e.target.value);
            }}
            className={`${inputClassName} pl-9 focus:outline-none focus:ring-2 focus:ring-primary-500`}
            placeholder={t("pointsOfInterest.form.addressPlaceholder")}
          />
        </div>
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          <i className="ri-magic-line mr-1"></i>
          {t("pointsOfInterest.form.addressAutocompleteHint")}
        </p>
      </div>

      {/* Latitud y Longitud */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label className={`block text-sm font-medium ${themeClasses.text}`}>
            {t("pointsOfInterest.fields.latitude")} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <i className={`ri-navigation-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              {...register("latitude", { required: true, valueAsNumber: true })}
              type="number"
              step="any"
              className={`${inputClassName} pl-9 ${errors.latitude
                ? "ring-2 ring-red-500"
                : "focus:ring-2 focus:ring-primary-500"
                } focus:outline-none`}
              placeholder="-33.4489"
              onChange={(e) => {
                const lat = parseFloat(e.target.value);
                if (!isNaN(lat)) {
                  onCoordinateChange(lat, longitude || 0);
                }
              }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label className={`block text-sm font-medium ${themeClasses.text}`}>
            {t("pointsOfInterest.fields.longitude")} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <i className={`ri-navigation-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              {...register("longitude", { required: true, valueAsNumber: true })}
              type="number"
              step="any"
              className={`${inputClassName} pl-9 ${errors.longitude
                ? "ring-2 ring-red-500"
                : "focus:ring-2 focus:ring-primary-500"
                } focus:outline-none`}
              placeholder="-70.6693"
              onChange={(e) => {
                const lng = parseFloat(e.target.value);
                if (!isNaN(lng)) {
                  onCoordinateChange(latitude || 0, lng);
                }
              }}
            />
          </div>
        </div>
      </div>
      <p className={`text-xs ${themeClasses.textSecondary} flex items-center gap-1`}>
        <i className="ri-information-line"></i>
        {t("pointsOfInterest.form.dragMarkerHint")}
      </p>

      {/* Mapa - Entre Dirección y Visibilidad */}
      {mapComponent && (
        <div className="w-full h-[250px] rounded-xl overflow-hidden border border-gray-200 dark:border-dark-border">
          {mapComponent}
        </div>
      )}
      {/* Coordenadas display */}
      {hasValidLocation && (
        <div className={`p-2 rounded-lg ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} text-xs ${themeClasses.textSecondary}`}>
          <span>Lat: {latitude?.toFixed(4)} | Lng: {longitude?.toFixed(4)}</span>
        </div>
      )}


      {/* Visibilidad - Rojo para privado, Azul para compartido */}
      <div className="flex flex-col gap-2">
        <label className={`block text-sm font-medium ${themeClasses.text}`}>
          {t("pointsOfInterest.fields.visibility")} <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => form.setValue("shared", false)}
            className={`flex flex-col items-start p-3 rounded-lg border transition-all ${!shared
              ? "border-red-500 bg-red-500/10"
              : `${themeClasses.cardBorder} ${themeClasses.cardBg}`
              }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${!shared ? "border-red-500" : themeClasses.cardBorder
                }`}>
                {!shared && <div className="w-2 h-2 rounded-full bg-red-500"></div>}
              </div>
              <i className={`ri-lock-line ${!shared ? "text-red-500" : themeClasses.textSecondary}`}></i>
              <span className={`font-medium ${!shared ? "text-red-500" : themeClasses.text}`}>
                {t("pointsOfInterest.visibility.private")}
              </span>
            </div>
            <span className={`text-xs ${themeClasses.textSecondary} mt-1 ml-6`}>
              {t("pointsOfInterest.visibility.privateDesc")}
            </span>
          </button>
          <button
            type="button"
            onClick={() => form.setValue("shared", true)}
            className={`flex flex-col items-start p-3 rounded-lg border transition-all ${shared
              ? "border-blue-500 bg-blue-500/10"
              : `${themeClasses.cardBorder} ${themeClasses.cardBg}`
              }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${shared ? "border-blue-500" : themeClasses.cardBorder
                }`}>
                {shared && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
              </div>
              <i className={`ri-group-line ${shared ? "text-blue-500" : themeClasses.textSecondary}`}></i>
              <span className={`font-medium ${shared ? "text-blue-500" : themeClasses.text}`}>
                {t("pointsOfInterest.visibility.shared")}
              </span>
            </div>
            <span className={`text-xs ${themeClasses.textSecondary} mt-1 ml-6`}>
              {t("pointsOfInterest.visibility.sharedDesc")}
            </span>
          </button>
        </div>
      </div>

      {/* Categoría */}
      <div className="flex flex-col gap-2">
        <label className={`block text-sm font-medium ${themeClasses.text}`}>
          {t("pointsOfInterest.fields.category")} ({t("pointsOfInterest.form.optional")})
        </label>
        <RHFController
          name="category_id"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={categoryOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("pointsOfInterest.form.selectCategory")}
              value={categoryOptions.find((opt) => opt.value === field.value) || null}
              onChange={(option: any) => field.onChange(option?.value || "")}
              isClearable
              isSearchable
              noOptionsMessage={() => t("pointsOfInterest.form.noCategoriesAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
      </div>

      {/* Notas adicionales */}
      <div className="flex flex-col gap-2">
        <label className={`block text-sm font-medium ${themeClasses.text}`}>
          {t("pointsOfInterest.fields.notes")} ({t("pointsOfInterest.form.optional")})
        </label>
        <textarea
          {...register("notes")}
          className={`${inputClassName} focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none`}
          rows={3}
          placeholder={t("pointsOfInterest.form.notesPlaceholder")}
        />
      </div>

      {/* Tip Box */}
      <div className={`p-4 rounded-lg ${isDarkMode ? "bg-blue-500/10" : "bg-blue-50"} border ${isDarkMode ? "border-blue-500/30" : "border-blue-200"}`}>
        <div className="flex items-start gap-3">
          <i className="ri-lightbulb-line text-blue-500 text-lg mt-0.5"></i>
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? "text-blue-400" : "text-blue-700"}`}>
              {t("pointsOfInterest.form.tipTitle")}
            </p>
            <p className={`text-xs ${isDarkMode ? "text-blue-300/80" : "text-blue-600"} mt-1`}>
              {t("pointsOfInterest.form.tipDescription")}
            </p>
          </div>
        </div>
      </div>

      {/* Botones */}
      <div className="flex space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className={`flex-1 text-xs 2xl:text-base py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-full ${themeClasses.buttonText} font-medium transition-colors`}
        >
          {t("pointsOfInterest.actions.cancel")}
        </button>
        <button
          type="submit"
          className={`flex-1 text-xs 2xl:text-base py-3 px-4 bg-primary-500 hover:bg-primary-400 text-white rounded-full font-medium transition-colors ${!isValid || (isEdit && !hasRealChanges()) || loadingSubmit || !hasValidLocation
            ? "opacity-50 cursor-not-allowed"
            : ""
            }`}
          disabled={!isValid || (isEdit && !hasRealChanges()) || loadingSubmit || !hasValidLocation}
        >
          {loadingSubmit
            ? isEdit
              ? t("pointsOfInterest.form.saving")
              : t("pointsOfInterest.form.creating")
            : isEdit
              ? t("pointsOfInterest.actions.saveChanges")
              : t("pointsOfInterest.actions.save")}
        </button>
      </div>
    </form>
  );
}
